import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../store';
import { createTicket, fetchAllUserTickets } from '../../../store/actions/HelpAction';
import { createSupportTicketSchema, buildYupSchema, sanitizeFormData } from '../../../utils/validation';
import BaseForm, { BaseFormFieldConfig } from '../../shared/FormComponents/BaseForm';
import { FormSchema } from '../../../utils/validation/form-schema';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, supportTicketFormTourSteps } from '../../shared/FeatureTour';

interface SupportTicketFormProps {
  onCancel: () => void;
}

const SupportTicketForm: React.FC<SupportTicketFormProps> = ({ onCancel }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { id: organizationId } = useSelector((state: RootState) => state.onboarding.selectedOrganization || { id: '' });

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'support-ticket-form',
    steps: supportTicketFormTourSteps,
    autoStartDelay: 600,
  });

  // Priority options for dropdown
  const priorityOptions = useMemo(
    () => [
      { value: 'low', label: t('Low') },
      { value: 'medium', label: t('Medium') },
      { value: 'high', label: t('High') },
      { value: 'critical', label: t('Critical') },
    ],
    [t]
  );

  // Create FormSchema for validation
  const schema = useMemo(() => {
    const globalSchema = createSupportTicketSchema();
    return new FormSchema('SupportTicketForm', globalSchema.getAllFields());
  }, []);

  // Form field configuration
  const fields: BaseFormFieldConfig[] = useMemo(
    () => [
      {
        name: 'title',
        type: 'input',
        label: t('Ticket Title'),
        placeholder: t('Brief summary of your issue'),
        required: true,
        gridSize: 'full',
      },
      {
        name: 'description',
        type: 'textarea',
        label: t('Description'),
        placeholder: t('Describe your issue in detail'),
        required: true,
        rows: 6,
        gridSize: 'full',
      },
      {
        name: 'priority',
        type: 'dropdown',
        label: t('Priority'),
        placeholder: t('Select Priority'),
        options: priorityOptions,
        customProps: { fullWidth: true },
        required: true,
        gridSize: 'full',
      },
      {
        name: 'files',
        type: 'fileUploader',
        label: t('Attachments'),
        gridSize: 'full',
        allowedExtensions: [],
        maxFileSize: 52428800, // 50MB
      },
    ],
    [t, priorityOptions]
  );

  // Initial values
  const initialValues = useMemo(
    () => ({
      title: '',
      description: '',
      priority: 'medium',
      files: [],
    }),
    []
  );

  // Handle form submission
  const handleSubmit = async (formValues: Record<string, any>) => {
    try {
      // Sanitize the validated form data
      const sanitizedValues = sanitizeFormData({
        title: formValues.title,
        description: formValues.description,
        priority: formValues.priority,
      });

      let submitData: any;
      const files = formValues.files || [];

      if (files.length > 0) {
        submitData = new FormData();
        submitData.append('title', String(sanitizedValues.title || ''));
        submitData.append('description', String(sanitizedValues.description || ''));
        submitData.append('priority', String(sanitizedValues.priority || ''));
        submitData.append('organization_id', organizationId);
        files.forEach((file: File) => submitData.append('files', file));
      } else {
        submitData = {
          title: String(sanitizedValues.title || ''),
          description: String(sanitizedValues.description || ''),
          priority: String(sanitizedValues.priority || ''),
          organization_id: organizationId,
        };
      }

      await dispatch(createTicket(submitData) as any);
      await dispatch(fetchAllUserTickets({ reset: true }) as any);
      onCancel();
    } catch (error) {
      console.error('Error creating ticket:', error);
      throw error;
    }
  };

  return (
    <div data-tour-id="support-ticket-form" className="h-[100vh]">
      <BaseForm
        title={t('Create New Support Ticket')}
        description={t('Fill out this form to add your support ticket.')}
        sections={fields}
        schema={schema}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        submitLabel={t('Submit Ticket')}
        cancelLabel={t('Cancel')}
        maxWidth="max-w-3xl"
      />
      <FeatureTour
        steps={supportTicketFormTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default SupportTicketForm;
